import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Share2, Copy, MessageCircle, Feather, Music, Lock, Gift, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/lang";
import { CHARACTERS } from "@/lib/characters";
import { CharacterCard, CharacterCardStyles } from "@/components/CharacterCard";

const COPY = {
  fr: {
    kicker: "Chatbot IA Gréco-Celtique",
    title: <>Des légendes qui <span className="italic" style={{ color: "var(--gold-glow)" }}>te répondent</span>.</>,
    intro: "Un barde qui te taquine, un druide qui t'apprend, Zeus qui te secoue, Apollon qui t'éclaire, Phanès qui te raconte la naissance du monde… Le Chat Gréco-Celtique réunit les grandes voix des mythes celtes et grecs pour t'accompagner sans jamais te diriger : rire, apprendre, réfléchir, t'apaiser — et repartir avec tes propres réponses.",
    share: "Partager avec quelqu'un", start: "Commencer à discuter", meet: "Les personnages",
    shareText: "Découvre Le Chat Gréco-Celtique : des personnages celtes et grecs qui discutent avec toi.",
    copied: "Lien copié !", copy: "Copier le lien",
    feats: [
      ["Des personnages avec du caractère", "Chacun a sa voix, son humour, sa culture et son univers."],
      ["Poèmes et musiques", "Berceuses, poèmes de joie, poèmes des éléments, musiques d'ambiance et lecture à voix haute."],
      ["100 % gratuit et illimité", "Aucun paiement, aucune inscription, aucun courriel demandé."],
      ["Tes échanges restent privés", "Conversations chiffrées et effacées automatiquement."],
    ],
  },
  en: {
    kicker: "Greco-Celtic AI chatbot",
    title: <>Legends that <span className="italic" style={{ color: "var(--gold-glow)" }}>answer you</span>.</>,
    intro: "A bard who teases you, a druid who teaches you, Zeus who shakes you, Apollo who enlightens you, Phanes who tells you how the world was born… Le Chat Gréco-Celtique gathers the great voices of Celtic and Greek myths to talk about your life, laugh, learn, find calm — and leave with your own answers.",
    share: "Share with someone", start: "Start chatting", meet: "The characters",
    shareText: "Discover Le Chat Gréco-Celtique: Celtic and Greek characters who chat with you.",
    copied: "Link copied!", copy: "Copy link",
    feats: [
      ["Characters with personality", "Each has its own voice, humour, culture and world."],
      ["Poems and music", "Lullabies, joy poems, elemental poems, ambient music and read-aloud."],
      ["100% free and unlimited", "No payment, no sign-up, no e-mail asked."],
      ["Your chats stay private", "Encrypted conversations, automatically erased."],
    ],
  },
};
const FEAT_ICONS = [Feather, Music, Gift, Lock];

const ShareBar = ({ c, tid }) => {
  const url = `${window.location.origin}/decouvrir`;
  const enc = encodeURIComponent;
  const links = [
    { id: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${enc(`${c.shareText} ${url}`)}` },
    { id: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { id: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${enc(c.shareText)}&url=${enc(url)}` },
    { id: "telegram", label: "Telegram", href: `https://t.me/share/url?url=${enc(url)}&text=${enc(c.shareText)}` },
  ];
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(c.copied); } catch { toast.error(url); }
  };
  const native = () => navigator.share?.({ title: "Le Chat Gréco-Celtique", text: c.shareText, url }).catch(() => {});
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${tid}-share-bar`}>
      {typeof navigator !== "undefined" && navigator.share && (
        <button type="button" onClick={native} data-testid={`${tid}-share-native`} className="btn-gold inline-flex items-center gap-2">
          <Share2 size={14} /> {c.share}
        </button>
      )}
      {links.map((l) => (
        <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`${tid}-share-${l.id}`}
           className="btn-ghost inline-flex items-center gap-2 !py-2.5 !px-4 !text-[11px]">
          <MessageCircle size={13} /> {l.label}
        </a>
      ))}
      <button type="button" onClick={copy} data-testid={`${tid}-share-copy`} className="btn-ghost inline-flex items-center gap-2 !py-2.5 !px-4 !text-[11px]">
        <Copy size={13} /> {c.copy}
      </button>
    </div>
  );
};

export default function Decouvrir() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.fr;
  return (
    <>
      <Navbar />
      <main data-testid="promo-page" className="relative pt-36 pb-20">
        <CharacterCardStyles />
        <section className="max-w-7xl mx-auto px-5 sm:px-8 fade-up">
          <div className="overline mb-5"><span className="kicker-line" />{c.kicker}</div>
          <h1 data-testid="promo-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] max-w-4xl">{c.title}</h1>
          <p className="mt-6 text-base sm:text-lg max-w-3xl leading-relaxed" style={{ color: "var(--violet-soft)" }}>{c.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/barde" data-testid="promo-start-btn" className="btn-gold inline-flex items-center gap-2">
              {c.start} <ArrowRight size={14} />
            </Link>
            <a href="#promo-personnages" data-testid="promo-meet-btn" className="btn-ghost inline-flex items-center gap-2">{c.meet}</a>
          </div>
          <div className="mt-8"><ShareBar c={c} tid="promo-top" /></div>
        </section>

        <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {c.feats.map(([t, d], i) => {
            const Icon = FEAT_ICONS[i];
            return (
              <div key={t} data-testid={`promo-feature-${i}`} className="mystic-card rounded-3xl p-6">
                <Icon size={18} className="text-gold" />
                <div className="mt-4 font-serif text-xl" style={{ color: "var(--star)" }}>{t}</div>
                <p className="mt-2 text-sm" style={{ color: "var(--violet-soft)" }}>{d}</p>
              </div>
            );
          })}
        </section>

        <section id="promo-personnages" className="max-w-7xl mx-auto px-5 sm:px-8 mt-24">
          <h2 className="font-serif text-3xl sm:text-4xl mb-8">{c.meet}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {CHARACTERS.map((ch) => <CharacterCard key={ch.id} c={ch} testPrefix="promo" />)}
          </div>
          <div className="mt-14 mystic-card gold-border rounded-3xl p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="font-serif text-2xl italic max-w-xl" style={{ color: "var(--star)" }}>
              {lang === "fr" ? "« Une bonne histoire se partage au coin du feu. »" : "\"A good story is shared by the fire.\""}
            </p>
            <ShareBar c={c} tid="promo-bottom" />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
